import { BEEF_AI_ACCESS_TOKEN, BEEF_AI_BASE_URL, beefAiGet } from "../common/utils/beefAiClient";
import { cadCurrencyFormat } from "../routes/pricing-reports/constants";
import { normalizePriceHistoryImages } from "./beefAiPricing";

export const LABEL_ANALYSIS_COLLECTION = "label_analysis";
export const LABEL_ANALYSIS_PRICE_HISTORY_ID_JSON_FIELD = "json(metadata, price_history_id)";
export const LABEL_ANALYSIS_PRICE_HISTORY_ID_JSON_ALIAS = "metadata_price_history_id_json";

const LIST_FIELDS = [
  "id",
  "date_created",
  "date_updated",
  "provider",
  "provider_id",
  "country_origin",
  "grade",
  "is_discounted",
  "is_halal",
  "is_kosher",
  "is_bulk",
  "is_previously_frozen",
  "has_canadian_beef_logo",
  "quality",
  "images",
  "product",
  "product_id.id",
  "product_id.name",
  "retailer",
  "unit_price",
  "unit",
  "unit_type",
  "price_history_id",
  LABEL_ANALYSIS_PRICE_HISTORY_ID_JSON_FIELD,
];

export const LABEL_ANALYSIS_DEFAULT_SORT = "created_desc";

/** Column id → Directus sort keys used by the list query. */
export const LABEL_ANALYSIS_SORTABLE_COLUMNS = {
  country_origin: { asc: "country_asc", desc: "country_desc" },
  grade: { asc: "grade_asc", desc: "grade_desc" },
  quality: { asc: "quality_asc", desc: "quality_desc" },
  is_discounted: { asc: "discounted_asc", desc: "discounted_desc" },
  is_halal: { asc: "halal_asc", desc: "halal_desc" },
  is_kosher: { asc: "kosher_asc", desc: "kosher_desc" },
  is_bulk: { asc: "bulk_asc", desc: "bulk_desc" },
  is_previously_frozen: { asc: "frozen_asc", desc: "frozen_desc" },
  has_canadian_beef_logo: { asc: "cdn_logo_asc", desc: "cdn_logo_desc" },
  provider: { asc: "provider_asc", desc: "provider_desc" },
  product: { asc: "product_asc", desc: "product_desc" },
  retailer: { asc: "retailer_asc", desc: "retailer_desc" },
  date_created: { asc: "created_asc", desc: "created_desc" },
};

const SORT_MAP = {
  created_desc: "-date_created",
  created_asc: "date_created",
  country_asc: "country_origin",
  country_desc: "-country_origin",
  grade_asc: "grade",
  grade_desc: "-grade",
  quality_asc: "quality",
  quality_desc: "-quality",
  discounted_asc: "is_discounted",
  discounted_desc: "-is_discounted",
  halal_asc: "is_halal",
  halal_desc: "-is_halal",
  kosher_asc: "is_kosher",
  kosher_desc: "-is_kosher",
  bulk_asc: "is_bulk",
  bulk_desc: "-is_bulk",
  frozen_asc: "is_previously_frozen",
  frozen_desc: "-is_previously_frozen",
  cdn_logo_asc: "has_canadian_beef_logo",
  cdn_logo_desc: "-has_canadian_beef_logo",
  provider_asc: "provider",
  provider_desc: "-provider",
  product_asc: "product",
  product_desc: "-product",
  retailer_asc: "retailer",
  retailer_desc: "-retailer",
};

export function getLabelAnalysisSortUi(sortBy) {
  for (const [columnId, directions] of Object.entries(LABEL_ANALYSIS_SORTABLE_COLUMNS)) {
    if (directions.asc === sortBy) {
      return { id: columnId, direction: "asc" };
    }
    if (directions.desc === sortBy) {
      return { id: columnId, direction: "desc" };
    }
  }
  return { id: "date_created", direction: "desc" };
}

export function nextLabelAnalysisSortBy(columnId, currentSortBy) {
  const columnSort = LABEL_ANALYSIS_SORTABLE_COLUMNS[columnId];
  if (!columnSort) {
    return currentSortBy;
  }
  const current = getLabelAnalysisSortUi(currentSortBy);
  const isAsc = current.id === columnId && current.direction === "asc";
  return columnSort[isAsc ? "desc" : "asc"];
}

export const LABEL_ANALYSIS_TRI_STATE_FILTER_CHOICES = [
  { value: "all", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unevaluated", label: "Not evaluated" },
];

/** Tri-state boolean columns shown and filterable on Location / Label. */
export const LABEL_ANALYSIS_BOOLEAN_FILTERS = [
  { key: "isDiscounted", field: "is_discounted", label: "Discounted" },
  { key: "isHalal", field: "is_halal", label: "Halal" },
  { key: "isKosher", field: "is_kosher", label: "Kosher" },
  { key: "isBulk", field: "is_bulk", label: "Bulk" },
  { key: "isPreviouslyFrozen", field: "is_previously_frozen", label: "Previously frozen" },
  { key: "hasCanadianBeefLogo", field: "has_canadian_beef_logo", label: "Canadian Beef logo" },
];

export function labelAnalysisConfigured() {
  return Boolean(BEEF_AI_BASE_URL && BEEF_AI_ACCESS_TOKEN);
}

export function formatLabelProvider(provider) {
  const raw = String(provider || "").trim();
  if (!raw) {
    return "";
  }
  const withoutPrefix = raw.replace(/^workflow:/i, "").replace(/[_-]+/g, " ");
  return withoutPrefix
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function formatCountryOriginLabel(code) {
  const raw = String(code || "").trim();
  if (!raw) {
    return "";
  }
  const iso = raw.toUpperCase();
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(iso);
    if (name && name !== iso) {
      return `${name} (${iso})`;
    }
  } catch (_) {
    /* invalid region code */
  }
  return iso;
}

export function normalizeTriStateBoolean(value) {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return null;
}

/**
 * @param {"all" | "yes" | "no" | "unevaluated" | ""} value
 * @param {string} field
 * @param {URLSearchParams} params
 */
export function applyTriStateBooleanFilter(params, field, value) {
  if (value === "yes") {
    params.set(`filter[${field}][_eq]`, "true");
    return;
  }
  if (value === "no") {
    params.set(`filter[${field}][_eq]`, "false");
    return;
  }
  if (value === "unevaluated") {
    params.set(`filter[${field}][_null]`, "true");
  }
}

/**
 * @param {URLSearchParams} params
 * @param {{
 *   countryOrigin?: string,
 *   grade?: string,
 *   quality?: string,
 *   provider?: string,
 *   product?: string,
 *   retailer?: string,
 *   isDiscounted?: string,
 *   isHalal?: string,
 *   isKosher?: string,
 *   isBulk?: string,
 *   isPreviouslyFrozen?: string,
 *   hasCanadianBeefLogo?: string,
 *   dateFrom?: string,
 *   dateTo?: string,
 * }} filters
 */
export function applyLabelAnalysisFilters(params, filters = {}) {
  if (filters.countryOrigin) {
    params.set("filter[country_origin][_eq]", filters.countryOrigin);
  }
  if (filters.grade) {
    params.set("filter[grade][_eq]", filters.grade);
  }
  if (filters.quality) {
    params.set("filter[quality][_eq]", filters.quality);
  }
  if (filters.provider) {
    params.set("filter[provider][_eq]", filters.provider);
  }
  if (filters.product) {
    params.set("filter[product][_eq]", filters.product);
  }
  if (filters.retailer) {
    params.set("filter[retailer][_eq]", filters.retailer);
  }
  if (filters.dateFrom) {
    params.set("filter[date_created][_gte]", filters.dateFrom);
  }
  if (filters.dateTo) {
    params.set("filter[date_created][_lte]", filters.dateTo);
  }
  LABEL_ANALYSIS_BOOLEAN_FILTERS.forEach(({ key, field }) => {
    applyTriStateBooleanFilter(params, field, filters[key]);
  });
}

export function normalizeLabelProductId(raw) {
  if (raw == null) {
    return "";
  }
  if (typeof raw === "object" && raw.id != null) {
    return String(raw.id);
  }
  return String(raw);
}

export function normalizeLabelProductName(row) {
  const related = row?.product_id;
  const relatedName =
    related && typeof related === "object" ? String(related.name || "").trim() : "";
  const storedName = row?.product ? String(row.product).trim() : "";
  return relatedName || storedName;
}

export function formatLinkedUnitPriceLabel(priceRow) {
  const unitPrice = Number(priceRow?.unit_price);
  if (!Number.isFinite(unitPrice)) {
    return "—";
  }
  const unit = typeof priceRow?.unit === "string" ? priceRow.unit.trim() : "";
  const formatted = cadCurrencyFormat.format(unitPrice);
  return unit ? `${formatted} per ${unit}` : formatted;
}

export function attachLinkedUnitPrice(normalizedRow, priceRow) {
  if (normalizedRow?.unitPriceValue != null) {
    return normalizedRow;
  }
  const unitPriceValue = Number(priceRow?.unit_price);
  return {
    ...normalizedRow,
    priceHistoryId: priceRow?.id
      ? String(priceRow.id)
      : normalizedRow.priceHistoryId || "",
    unitPriceValue: Number.isFinite(unitPriceValue) ? unitPriceValue : null,
    unitPriceLabel: formatLinkedUnitPriceLabel(priceRow),
    unit: typeof priceRow?.unit === "string" ? priceRow.unit.trim() : "",
  };
}

export function normalizeLabelAnalysisRow(row) {
  const countryOrigin = row?.country_origin ? String(row.country_origin).trim() : "";
  const productName = normalizeLabelProductName(row);
  const retailer = row?.retailer ? String(row.retailer).trim() : "";
  const unitPriceValue = Number(row?.unit_price);
  const hasOwnUnitPrice = Number.isFinite(unitPriceValue);
  return {
    id: row?.id || "",
    dateCreated: row?.date_created || null,
    dateUpdated: row?.date_updated || null,
    provider: row?.provider || "",
    providerLabel: formatLabelProvider(row?.provider) || "—",
    providerId: row?.provider_id || "",
    countryOrigin,
    countryLabel: formatCountryOriginLabel(countryOrigin) || "—",
    grade: row?.grade ? String(row.grade).trim() : "",
    quality: row?.quality ? String(row.quality).trim() : "",
    product: row?.product ? String(row.product).trim() : "",
    productId: normalizeLabelProductId(row?.product_id),
    productName: productName || "—",
    retailer,
    retailerLabel: retailer || "—",
    isDiscounted: normalizeTriStateBoolean(row?.is_discounted),
    isHalal: normalizeTriStateBoolean(row?.is_halal),
    isKosher: normalizeTriStateBoolean(row?.is_kosher),
    isBulk: normalizeTriStateBoolean(row?.is_bulk),
    isPreviouslyFrozen: normalizeTriStateBoolean(row?.is_previously_frozen),
    hasCanadianBeefLogo: normalizeTriStateBoolean(row?.has_canadian_beef_logo),
    images: normalizePriceHistoryImages(row?.images),
    priceHistoryId: extractLabelPriceHistoryId(row),
    unitPriceValue: hasOwnUnitPrice ? unitPriceValue : null,
    unitPriceLabel: hasOwnUnitPrice ? formatLinkedUnitPriceLabel(row) : "—",
    unit: typeof row?.unit === "string" ? row.unit.trim() : "",
  };
}

function distinctValuesFromAggregateRows(rows, field) {
  const seen = new Set();
  const values = [];
  for (const row of rows || []) {
    const raw = row?.[field];
    if (raw == null) {
      continue;
    }
    const value = String(raw).trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    values.push(value);
  }
  return values;
}

async function fetchDistinctFieldValues(field) {
  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("aggregate[count]", "*");
  params.append("groupBy[]", field);
  params.set("limit", "-1");
  params.set("sort", field);

  const json = await beefAiGet(`/items/${LABEL_ANALYSIS_COLLECTION}?${params.toString()}`);
  return distinctValuesFromAggregateRows(json?.data, field);
}

/**
 * Distinct filter values currently present on `label_analysis`.
 */
export async function fetchLabelAnalysisFilterOptions() {
  if (!labelAnalysisConfigured()) {
    throw new Error(
      "Location label report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }

  const [countries, grades, qualities, products, retailers] = await Promise.all([
    fetchDistinctFieldValues("country_origin"),
    fetchDistinctFieldValues("grade"),
    fetchDistinctFieldValues("quality"),
    fetchDistinctFieldValues("product"),
    fetchDistinctFieldValues("retailer"),
  ]);

  return { countries, grades, qualities, products, retailers };
}

/**
 * Paginated `label_analysis` rows from the Beef AI Directus host.
 */
export async function fetchLabelAnalysisRows({
  page = 1,
  limit = 10,
  sortBy = LABEL_ANALYSIS_DEFAULT_SORT,
  countryOrigin = "",
  grade = "",
  quality = "",
  provider = "",
  product = "",
  retailer = "",
  isDiscounted = "all",
  isHalal = "all",
  isKosher = "all",
  isBulk = "all",
  isPreviouslyFrozen = "all",
  hasCanadianBeefLogo = "all",
  dateFrom = "",
  dateTo = "",
} = {}) {
  if (!labelAnalysisConfigured()) {
    throw new Error(
      "Location label report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }

  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("page", String(page));
  params.set("limit", String(limit));
  params.set("sort", SORT_MAP[sortBy] || SORT_MAP[LABEL_ANALYSIS_DEFAULT_SORT]);
  LIST_FIELDS.forEach((field) => params.append("fields[]", field));
  params.append("meta[]", "filter_count");
  applyLabelAnalysisFilters(params, {
    countryOrigin,
    grade,
    quality,
    provider,
    product,
    retailer,
    isDiscounted,
    isHalal,
    isKosher,
    isBulk,
    isPreviouslyFrozen,
    hasCanadianBeefLogo,
    dateFrom,
    dateTo,
  });

  const json = await beefAiGet(`/items/${LABEL_ANALYSIS_COLLECTION}?${params.toString()}`);
  const rawRows = json?.data || [];
  const filterCountFromMeta = typeof json?.meta?.filter_count === "number";
  const normalizedRows = rawRows.map(normalizeLabelAnalysisRow);
  const missingPriceIds = normalizedRows
    .filter((row) => row.unitPriceValue == null)
    .map((row) => row.priceHistoryId);
  const priceById = await fetchPriceHistoryRowsByIds(missingPriceIds);

  return {
    rows: normalizedRows.map((row) => attachLinkedUnitPrice(row, priceById.get(row.priceHistoryId))),
    total: filterCountFromMeta ? json.meta.filter_count : (page - 1) * limit + rawRows.length,
  };
}

const PRICE_LINK_PAGE_SIZE = 100;
const PRICE_LINK_MAX_PAGES = 50;
const PRICE_HISTORY_ID_CHUNK_SIZE = 80;

function parseMetadata(metadata) {
  if (!metadata) {
    return null;
  }
  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return typeof metadata === "object" ? metadata : null;
}

export function extractLabelPriceHistoryId(row) {
  const fromColumn = typeof row?.price_history_id === "string" ? row.price_history_id.trim() : "";
  if (fromColumn) {
    return fromColumn;
  }
  const fromJson = row?.[LABEL_ANALYSIS_PRICE_HISTORY_ID_JSON_ALIAS];
  if (typeof fromJson === "string" && fromJson.trim()) {
    return fromJson.trim();
  }
  if (fromJson != null && typeof fromJson !== "object") {
    const asString = String(fromJson).trim();
    if (asString) {
      return asString;
    }
  }
  const fromMeta = parseMetadata(row?.metadata)?.price_history_id;
  if (typeof fromMeta === "string" && fromMeta.trim()) {
    return fromMeta.trim();
  }
  return "";
}

export function isComparableLinkedUnitPrice(row) {
  const unitPrice = Number(row?.unit_price);
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
    return false;
  }
  const unit = String(row?.unit || "").trim().toLowerCase();
  const unitType = String(row?.unit_type || "").trim().toLowerCase();
  if (unit && unit !== "kg") {
    return false;
  }
  if (unitType && unitType !== "weight") {
    return false;
  }
  return true;
}

function chunkValues(values, size) {
  const chunks = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}

/**
 * @param {{ countryOrigin?: string, priceHistoryId?: string }[]} links
 * @param {Map<string, { unit_price?: number, unit?: string, unit_type?: string }>} priceById
 */
export function aggregateAveragePriceByCountry(links, priceById) {
  const byCountry = new Map();

  for (const link of links || []) {
    const country = String(link?.countryOrigin || "").trim();
    const priceHistoryId = String(link?.priceHistoryId || "").trim();
    if (!country || !priceHistoryId) {
      continue;
    }
    const priceRow = priceById instanceof Map ? priceById.get(priceHistoryId) : null;
    if (!isComparableLinkedUnitPrice(priceRow)) {
      continue;
    }
    const current = byCountry.get(country) || { country, sum: 0, count: 0 };
    current.sum += Number(priceRow.unit_price);
    current.count += 1;
    byCountry.set(country, current);
  }

  return [...byCountry.values()]
    .map((row) => ({
      country: row.country,
      countryLabel: formatCountryOriginLabel(row.country) || row.country,
      avgUnitPrice: row.sum / row.count,
      count: row.count,
    }))
    .sort((a, b) => {
      if (b.avgUnitPrice !== a.avgUnitPrice) {
        return b.avgUnitPrice - a.avgUnitPrice;
      }
      return a.countryLabel.localeCompare(b.countryLabel);
    });
}

async function fetchLabelAnalysisPriceLinks(filters) {
  const links = [];

  for (let page = 1; page <= PRICE_LINK_MAX_PAGES; page += 1) {
    const params = new URLSearchParams();
    params.set("access_token", BEEF_AI_ACCESS_TOKEN);
    params.set("page", String(page));
    params.set("limit", String(PRICE_LINK_PAGE_SIZE));
    params.append("fields[]", "id");
    params.append("fields[]", "country_origin");
    params.append("fields[]", LABEL_ANALYSIS_PRICE_HISTORY_ID_JSON_FIELD);
    applyLabelAnalysisFilters(params, filters);

    const json = await beefAiGet(`/items/${LABEL_ANALYSIS_COLLECTION}?${params.toString()}`);
    const batch = json?.data || [];
    for (const row of batch) {
      const countryOrigin = row?.country_origin ? String(row.country_origin).trim() : "";
      const priceHistoryId = extractLabelPriceHistoryId(row);
      if (!countryOrigin || !priceHistoryId) {
        continue;
      }
      links.push({ countryOrigin, priceHistoryId });
    }
    if (batch.length < PRICE_LINK_PAGE_SIZE) {
      break;
    }
  }

  return links;
}

async function fetchPriceHistoryRowsByIds(ids) {
  const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
  const priceById = new Map();
  if (!uniqueIds.length) {
    return priceById;
  }

  for (const chunk of chunkValues(uniqueIds, PRICE_HISTORY_ID_CHUNK_SIZE)) {
    const params = new URLSearchParams();
    params.set("access_token", BEEF_AI_ACCESS_TOKEN);
    params.set("limit", String(chunk.length));
    params.set("filter[id][_in]", chunk.join(","));
    params.append("fields[]", "id");
    params.append("fields[]", "unit_price");
    params.append("fields[]", "unit");
    params.append("fields[]", "unit_type");

    const json = await beefAiGet(`/items/price_history?${params.toString()}`);
    for (const row of json?.data || []) {
      if (row?.id) {
        priceById.set(String(row.id), row);
      }
    }
  }

  return priceById;
}

/**
 * Average linked `price_history.unit_price` (kg) by `label_analysis.country_origin`.
 * Requires a product filter. Uses `metadata.price_history_id` (JSON), not a Directus relation.
 *
 * @param {Parameters<typeof applyLabelAnalysisFilters>[1]} filters
 * @returns {Promise<{ country: string, countryLabel: string, avgUnitPrice: number, count: number }[]>}
 */
export async function fetchAveragePriceByCountryOrigin(filters = {}) {
  if (!labelAnalysisConfigured()) {
    throw new Error(
      "Location label report is not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }
  if (!filters.product) {
    return [];
  }

  const links = await fetchLabelAnalysisPriceLinks(filters);
  const priceById = await fetchPriceHistoryRowsByIds(links.map((link) => link.priceHistoryId));
  return aggregateAveragePriceByCountry(links, priceById);
}
